import {useForm} from '@tanstack/react-form';
import React from 'react';
import {Button} from '~/components/Button';
import {InputType} from '~/components/Controls/Input';
import {useConfirm} from '~/components/Dialogs';
import formStyle from '~/components/Form/index.module.scss';
import {
    CheckboxField,
    EditorField,
    FormColumn,
    FormControls,
    FormRow,
    FormTitle,
    hasErrors,
    IFormProps,
    InputField,
    IntentText,
    SelectField,
    TagsField
} from '~/components/Form';
import {InfoPopover} from '~/components/InfoPopover';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {TabsWithBody} from '~/components/Tabs';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {Intent} from '~/lib/intent';
import {useAbility} from '~/lib/ability';
import {FillFromRulesButton} from '~/modules/Bidders/components/FillFromRulesButton';
import {PlacementFormatsBlock} from '~/modules/Bidders/components/PlacementFormatsBlock';
import {SelectBidderEncodingField} from '~/modules/Bidders/components/SelectBidderEncoding';
import {SelectBidderProtocolField} from '~/modules/Bidders/components/SelectBidderProtocol';
import {SelectBidderStatusField} from '~/modules/Bidders/components/SelectBidderStatus';
import {SelectDcField} from '~/modules/Bidders/components/SelectDc';
import {SelectFloorTypeField} from '~/modules/Bidders/components/SelectFloorType';
import {BidderFormCtx, clearFieldSubmitError, Field, getFormBridgeErrors} from '~/modules/Bidders/forms/BidderFormBridge';
import {gpidRequirementItems, IBidder, tagIdRequirementItems} from '~/modules/Bidders/models/Bidder';
import {toBidderFormValues, toBidderSubmitValues} from '~/modules/Bidders/models/BidderFormMapper';
import {BidderFormValues, createBidderFormSchema} from '~/modules/Bidders/models/BidderFormSchema';
import {ClientsAbility} from '~/modules/Clients/ability';


export interface IBidderForm {
    bidder?: IBidder;
    onSubmit?: (values: IBidder, formActions: IBidderFormActions, close: IModalComponentClose) => void | Promise<void>;
    activeSection?: 'general' | 'integration' | 'spendCapping' | 'mock';
}

export interface IBidderFormActions {
    setSubmitting: (submitting: boolean) => void;
    setStatus: (nextStatus: string | undefined) => void;
}

const FormShell = (props: IFormProps) => {
    const classes = [formStyle.form, props.className].filter(Boolean);
    const childrenArray = React.Children.toArray(props.children);
    const titles: React.ReactNode[] = [];
    const content: React.ReactNode[] = [];
    const controls: React.ReactNode[] = [];

    childrenArray.forEach(child => {
        if (!React.isValidElement(child)) {
            content.push(child);
            return;
        }
        if (child.type === FormControls) {
            controls.push(child);
        } else if (child.type === FormTitle) {
            titles.push(child);
        } else {
            content.push(child);
        }
    });

    return <form {...props} className={classes.join(' ')}>
        {titles}
        <div className={formStyle.formBody}>
            {content}
        </div>
        {controls}
    </form>;
};

export const BidderForm: IModalComponent<IBidderForm> = props => {
    const ability = useAbility(ClientsAbility);
    const confirm = useConfirm();
    const [status, setStatus] = React.useState<string>();
    const [proposalNotes, setProposalNotes] = React.useState<string[]>([]);
    const closeModal = props.close ?? (() => undefined);

    const bidderForm = useForm({
        defaultValues: toBidderFormValues(props.bidder),
        validators: {
            onSubmit: createBidderFormSchema(ability.isAdmin()) as any
        },
        onSubmit: async ({value}) => {
            const bidder = toBidderSubmitValues(value);

            const submit = async () => {
                if (!props.onSubmit) return;
                const formActions: IBidderFormActions = {
                    setSubmitting: (_submitting: boolean) => undefined,
                    setStatus
                };

                await Promise.resolve(props.onSubmit(bidder, formActions, closeModal));
            };

            if (bidder.protocol === 'mock') {
                const approved = await new Promise<boolean>(resolve => {
                    confirm('Are you sure you want to save the bidder with mock protocol?', {
                        onOk: () => resolve(true),
                        onNo: () => resolve(false),
                        icon: <i className='fa fa-exclamation' />,
                        intent: Intent.DANGER
                    });
                });
                if (!approved) return;
            }

            try {
                setStatus(undefined);
                await submit();
            } catch (error) {
                setStatus(getApiErrorMessage(error));
                throw error;
            }
        }
    });

    return <bidderForm.Subscribe selector={state => state}>
        {state => {
            const setFieldValue = (fieldName: string, fieldValue: unknown) => {
                setStatus(undefined);
                bidderForm.setFieldValue(fieldName as never, fieldValue as never);
                clearFieldSubmitError(bidderForm, fieldName);
            };

            const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
                event?.preventDefault();
                void bidderForm.handleSubmit();
            };

            const fieldErrors = getFormBridgeErrors(bidderForm);
            const formBridge = {
                errors: fieldErrors,
                touched: {},
                submitCount: state.submissionAttempts,
                setFieldValue,
                setStatus,
                setSubmitting: (_submitting: boolean) => undefined,
            };

            const bidder = state.values as BidderFormValues;
            const isSubmitting = state.isSubmitting;

            let initialTabIndex = 0;
            if (props.activeSection === 'integration') {
                initialTabIndex = 1;
            } else if (props.activeSection === 'spendCapping') {
                initialTabIndex = 3;
            } else if (props.activeSection === 'mock' && bidder.protocol === 'mock') {
                initialTabIndex = 4;
            }

            return (
                <BidderFormCtx.Provider value={{form: bidderForm, formBridge}}>
                    <FormShell onSubmit={handleSubmit}>
                        <FormTitle>
                            {bidder.id ? 'Edit bidder' : 'Create bidder'}
                        </FormTitle>
                        {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
                        <TabsWithBody
                            initialIndex={initialTabIndex}
                            tabs={[{
                                title: 'General',
                                intent: hasErrors(formBridge as any, ['title', 'maxRpm',
                                    'tags', 'coppaFlag', 'useAdMergeType', 'excludeFromNonInBannerAuction', 'auctionCategory', 'adControl',
                                    'delayedNotification', 'minBidFloor', 'maxBidFloor', 'status', 'impExpirationTime',
                                    'clientNurl', 'clientLurl', 'healthyAvgPrice', 'tagIdRequirement',
                                    'gpidRequirement']) ? Intent.DANGER : undefined,
                                children: (
                                    <FormRow noBorder>
                                        <FormColumn>
                                            <Field
                                                name='title'
                                                label='Title'
                                                required
                                                options={{
                                                    placeholder: 'Title',
                                                    disabled: isSubmitting
                                                }}
                                                component={InputField}
                                            />
                                            <Field
                                                name='auctionCategory'
                                                label='Auction Category'
                                                required
                                                options={{
                                                    placeholder: 'Auction Category',
                                                    disabled: isSubmitting,
                                                    items: [{
                                                        label: 'performance',
                                                        value: 'performance'
                                                    }, {
                                                        label: 'branded',
                                                        value: 'branded'
                                                    }]
                                                }}
                                                component={SelectField}
                                            />
                                            <Field
                                                name='maxRpm'
                                                label='Max QPS'
                                                required
                                                type='number'
                                                options={{
                                                    placeholder: 'Max QPS',
                                                    type: InputType.INTEGER,
                                                    disabled: isSubmitting,
                                                    min: 0,
                                                    step: 1
                                                }}
                                                component={InputField}
                                            />
                                            <Field
                                                name='tags'
                                                label='Tags'
                                                options={{
                                                    placeholder: 'Tags',
                                                    disabled: isSubmitting,
                                                    dropdown: true
                                                }}
                                                component={TagsField}
                                            />
                                        </FormColumn>
                                        <FormColumn>
                                            <Field
                                                name='useAdMergeType'
                                                label={<>Merge Ad Types {bidder.placementFormats && <InfoPopover className={formStyle.labelInfo} content='Not used while a formats table is set' ariaLabel='Not used while a formats table is set' width={240}/>}</>}
                                                component={CheckboxField}
                                                options={{placeholder: 'Merge Ad Types', disabled: isSubmitting || bidder.placementFormats != null}}
                                            />
                                            <Field
                                                name='healthyAvgPrice'
                                                label='RPM guidance'
                                                options={{
                                                    placeholder: '0.00',
                                                    type: InputType.FLOAT,
                                                    disabled: isSubmitting,
                                                    min: 0,
                                                    step: 0.01
                                                }}
                                                component={InputField}
                                            />
                                            <Field
                                                name='coppaFlag'
                                                label='Coppa'
                                                component={CheckboxField}
                                                options={{
                                                    placeholder: 'Coppa',
                                                    disabled: isSubmitting
                                                }}
                                            />
                                            <Field
                                                name='excludeFromNonInBannerAuction'
                                                label={<>Exclude from non in-banner auction {bidder.placementFormats && <InfoPopover className={formStyle.labelInfo} content='Not used while a formats table is set' ariaLabel='Not used while a formats table is set' width={240}/>}</>}
                                                component={CheckboxField}
                                                options={{
                                                    placeholder: 'Exclude from non in-banner auction',
                                                    disabled: isSubmitting || bidder.placementFormats != null
                                                }}
                                            />
                                            <Field
                                                name='adControl'
                                                label='Ad Control'
                                                component={CheckboxField}
                                                options={{
                                                    placeholder: 'Ad Control',
                                                    disabled: isSubmitting
                                                }}
                                            />
                                            <Field
                                                name='delayedNotification'
                                                label='Delayed Notification'
                                                component={CheckboxField}
                                                options={{
                                                    placeholder: 'Delayed Notification',
                                                    disabled: isSubmitting
                                                }}
                                            />
                                            <Field
                                                name='minBidFloor'
                                                label='Min Floor'
                                                options={{
                                                    placeholder: '0.00',
                                                    type: InputType.FLOAT,
                                                    disabled: isSubmitting,
                                                    min: 0,
                                                    step: 0.01,
                                                }}
                                                component={InputField}
                                            />
                                            <Field
                                                name='maxBidFloor'
                                                label='Max Floor'
                                                options={{
                                                    placeholder: '0.00',
                                                    type: InputType.FLOAT,
                                                    disabled: isSubmitting,
                                                    min: 0,
                                                    step: 0.01,
                                                }}
                                                component={InputField}
                                            />
                                            <Field
                                                name='status'
                                                label='Status'
                                                options={{
                                                    placeholder: 'Status',
                                                    disabled: isSubmitting
                                                }}
                                                component={SelectBidderStatusField}
                                            />
                                            <Field
                                                name='impExpirationTime'
                                                label='Expiration time'
                                                type='number'
                                                options={{
                                                    placeholder: 'Expiration time',
                                                    type: InputType.INTEGER,
                                                    disabled: isSubmitting,
                                                    min: 0,
                                                    step: 1
                                                }}
                                                component={InputField}
                                            />
                                            <Field
                                                name='clientNurl'
                                                label='Client NURL'
                                                component={CheckboxField}
                                                options={{
                                                    placeholder: 'Client NURL',
                                                    disabled: isSubmitting
                                                }}
                                            />
                                            <Field
                                                name='clientLurl'
                                                label='Client LURL'
                                                component={CheckboxField}
                                                options={{
                                                    placeholder: 'Client LURL',
                                                    disabled: isSubmitting
                                                }}
                                            />
                                            <Field
                                                name='tagIdRequirement'
                                                label='TagID requirement'
                                                options={{
                                                    placeholder: 'TagID requirement',
                                                    disabled: isSubmitting,
                                                    items: tagIdRequirementItems
                                                }}
                                                component={SelectField}
                                            />
                                            <Field
                                                name='gpidRequirement'
                                                label='GPID requirement'
                                                options={{
                                                    placeholder: 'GPID requirement',
                                                    disabled: isSubmitting,
                                                    items: gpidRequirementItems
                                                }}
                                                component={SelectField}
                                            />
                                        </FormColumn>
                                    </FormRow>
                                )
                            }, {
                                title: 'Integration',
                                intent: hasErrors(formBridge as any, ['protocol', 'encoding', 'endpoint', 'dc', 'wseat', 'bseat', 'floorType', 'skAnIdCheck', 'skAnId', 'passCalloutRound', 'placementFormats']) ? Intent.DANGER : undefined,
                                children: <FormRow noBorder>
                                    <FormColumn>
                                        <Field
                                            name='protocol'
                                            label='Protocol'
                                            options={{
                                                placeholder: 'Protocol',
                                                disabled: isSubmitting,
                                                searchable: true,
                                            }}
                                            component={SelectBidderProtocolField}
                                        />
                                        <Field
                                            name='encoding'
                                            label='Encoding'
                                            options={{
                                                placeholder: 'Default by protocol',
                                                disabled: isSubmitting,
                                                clearable: true,
                                            }}
                                            component={SelectBidderEncodingField}
                                        />
                                        <Field
                                            name='endpoint'
                                            options={{
                                                placeholder: 'Endpoint',
                                                disabled: isSubmitting
                                            }}
                                            label='Endpoint'
                                            required
                                            component={InputField}
                                        />
                                        <Field
                                            name='dc'
                                            label='Datacenters'
                                            options={{
                                                placeholder: 'Datacenters',
                                                disabled: isSubmitting
                                            }}
                                            component={SelectDcField}
                                        />
                                        <Field
                                            name='wseat'
                                            label='wseat'
                                            options={{
                                                mode: 'text',
                                                disabled: isSubmitting,
                                                minLines: 6,
                                                maxLines: 20
                                            }}
                                            component={EditorField}
                                        />
                                        <Field
                                            name='bseat'
                                            label='bseat'
                                            options={{
                                                mode: 'text',
                                                disabled: isSubmitting,
                                                minLines: 6,
                                                maxLines: 20
                                            }}
                                            component={EditorField}
                                        />
                                        <Field
                                            name='floorType'
                                            label='Floor type'
                                            required
                                            options={{
                                                placeholder: 'No floor',
                                                disabled: isSubmitting
                                            }}
                                            component={SelectFloorTypeField}
                                        />
                                        <Field
                                            name='skAnIdCheck'
                                            label='Check for SK AN ID'
                                            component={CheckboxField}
                                            options={{
                                                placeholder: 'Check for SK AN ID',
                                                disabled: isSubmitting
                                            }}
                                        />
                                        <Field
                                            name='skAnId'
                                            label='SK AN ID'
                                            options={{
                                                placeholder: 'SK AN ID',
                                                disabled: isSubmitting
                                            }}
                                            component={InputField}
                                        />
                                        <Field
                                            name='passCalloutRound'
                                            label='Pass Callout Round'
                                            component={CheckboxField}
                                            options={{
                                                placeholder: 'Pass Callout Round',
                                                disabled: isSubmitting
                                            }}
                                        />
                                    </FormColumn>
                                </FormRow>
                            }, {
                                title: 'Formats per placement',
                                children: (
                                    <FormRow noBorder noMargin>
                                        <FormColumn noBorder>
                                            <PlacementFormatsBlock
                                                editing
                                                disabled={isSubmitting}
                                                table={bidder.placementFormats}
                                                multiFormatRequest={!!bidder.multiFormatRequest}
                                                onChangeTable={next => { setFieldValue('placementFormats', next); if (next === null) setProposalNotes([]); }}
                                                onChangeMultiFormatRequest={next => setFieldValue('multiFormatRequest', next)}
                                                error={fieldErrors.placementFormats}
                                                notes={proposalNotes}
                                                actions={bidder.id != null
                                                    ? <FillFromRulesButton bidderId={bidder.id} disabled={isSubmitting} onProposal={proposal => {
                                                        setFieldValue('placementFormats', proposal.placementFormats);
                                                        setFieldValue('multiFormatRequest', proposal.multiFormatRequest);
                                                        setProposalNotes(proposal.notes);
                                                    }}/>
                                                    : undefined}
                                            />
                                        </FormColumn>
                                    </FormRow>
                                )
                            }, {
                                title: 'Fallback cap',
                                intent: hasErrors(formBridge as any, ['spendCapping']) ? Intent.DANGER : undefined,
                                children: <FormRow noBorder>
                                    <FormColumn>
                                        <Field
                                            name='spendCapping.limit'
                                            label='USD per hour'
                                            help="At least $100. Seeds a bidder's spend limits and holds only when no plan is possible. Saving it changes no existing limits."
                                            type='number'
                                            options={{
                                                placeholder: 'USD per hour',
                                                type: InputType.FLOAT,
                                                disabled: isSubmitting,
                                                min: 100,
                                                step: 0.1
                                            }}
                                            component={InputField}
                                        />
                                        <Field
                                            name='spendCapping.test'
                                            label='Test'
                                            component={CheckboxField}
                                            options={{
                                                placeholder: 'Test',
                                                disabled: isSubmitting
                                            }}
                                        />
                                    </FormColumn>
                                </FormRow>
                            },
                            bidder.protocol === 'mock' ? {
                                title: 'Mock bid response',
                                intent: hasErrors(formBridge as any, ['mockBidResponse']) ? Intent.DANGER : undefined,
                                children: <FormRow noBorder>
                                    <FormColumn>
                                        <Field
                                            name='mockBidResponse'
                                            options={{
                                                mode: 'json',
                                                disabled: isSubmitting,
                                                canFullScreen: true
                                            }}
                                            component={EditorField}
                                        />
                                    </FormColumn>
                                </FormRow>
                            } : undefined].filter(tab => !!tab) as any}
                        />
                        <FormControls>
                            <Button
                                disabled={isSubmitting}
                                onClick={props.close}
                            >Cancel</Button>
                            <Button
                                type='submit'
                                disabled={isSubmitting}
                                intent={Intent.PRIMARY}
                                rightIcon={isSubmitting && <i className='fa fa-spinner fa-spin' />}
                            >Save</Button>
                        </FormControls>
                    </FormShell>
                </BidderFormCtx.Provider>
            );
        }}
    </bidderForm.Subscribe>;
};
